/**
 * dcent-tap-to-sign.js — Phase 50 탭 서명 아코디언 (5초 자동 전환 + 진행 바).
 *
 * 3-스텝 탭이 DURATION(5000ms)마다 다음 탭으로 자동 전환된다. 활성 탭 좌측의 진행 바가
 * tab-progress 키프레임(scaleY 0→1)으로 5초 동안 차오르고, 스텝 이미지는 crossfade된다.
 * 소스: ~/work/homepage-design-src/src/components/TapToSign.tsx L54-59(자동 전환),
 * L102-109(진행 바 리마운트)를 그대로 이식(D-01).
 *   - 화면 진입(IntersectionObserver threshold 0.3) 시 자동 전환 시작(1회).
 *   - 클릭 = 수동 전환(setActive) + 재스케줄.
 *   - 진행 바 재시작 = animation "none" → 강제 reflow(void offsetWidth) → tab-progress 재적용.
 *
 * reduced-motion(D-01 폴백): 자동 전환 타이머 비활성(수동 클릭은 동작), 진행 바 애니메이션 없음,
 * 스텝 1 노출. data-section-id 로 스코프된 root 안에서만 [data-dcent-taptosign] → [data-tab]/
 * [data-tab-img]/[data-progress] 를 질의해 다중 인스턴스가 독립 동작한다.
 * typeof window 가드로 import 시 부수효과 0(vitest 안전).
 *
 * Markup:
 *   <section class="dcent-tap-to-sign dcent-reveal" ...>
 *     <div class="dcent-tap-to-sign__grid" data-dcent-taptosign>
 *       …[data-tab-img] × 3 (crossfade, .is-active = 노출)
 *       <button class="dcent-tap-to-sign__tab" data-tab>
 *         <span class="dcent-tap-to-sign__progress" data-progress></span>  // 좌측 진행 바
 *         …번호 칩 + 제목 + 설명
 *       </button> × 3
 *     </div>
 *   </section>
 */

const DURATION = 5000; // 탭 자동 전환 주기(ms)

export function initTapToSign(root = document) {
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  root.querySelectorAll("[data-dcent-taptosign]").forEach((el) => {
    const tabs = Array.from(el.querySelectorAll("[data-tab]"));
    const imgs = Array.from(el.querySelectorAll("[data-tab-img]"));
    if (tabs.length === 0) return;
    let active = 0;
    let timer = 0;
    let started = false;

    // 진행 바를 처음부터 재생: animation "none" → 강제 reflow → tab-progress 재적용.
    const restartProgress = (tab) => {
      const bar = tab.querySelector("[data-progress]");
      if (!bar) return;
      bar.style.animation = "none";
      void bar.offsetWidth;
      bar.style.animation = reduce
        ? "none"
        : `tab-progress ${DURATION}ms linear forwards`;
    };

    // DURATION 뒤 다음 탭으로 자동 전환 예약(재예약 시 기존 타이머 취소).
    const schedule = () => {
      clearTimeout(timer);
      timer = window.setTimeout(
        () => setActive((active + 1) % tabs.length),
        DURATION,
      );
    };

    // 탭 i 활성화: is-active 토글(탭/이미지) + 진행 바 재시작 + (모션 허용 시) 재예약.
    const setActive = (i) => {
      active = i;
      tabs.forEach((t, k) => t.classList.toggle("is-active", k === i));
      imgs.forEach((m, k) => m.classList.toggle("is-active", k === i));
      restartProgress(tabs[i]);
      if (!reduce) schedule();
    };

    tabs.forEach((t, i) => {
      t.addEventListener("click", () => setActive(i));
    });

    // 화면 진입 시 1회 자동 전환 시작(threshold 0.3).
    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started) {
            started = true;
            setActive(0);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.3 },
    );
    io.observe(el);
  });
}

if (typeof window !== "undefined" && typeof document !== "undefined") {
  document.addEventListener("DOMContentLoaded", () => initTapToSign());
  // 테마 편집기가 섹션 DOM을 AJAX 교체할 때 재초기화(라이브 스토어는 미발생). e.target 스코프로 재실행.
  document.addEventListener("shopify:section:load", (e) =>
    initTapToSign(e.target),
  );
}
